/**
 * Structured-data helpers for Vanturis.
 *
 * Rules:
 *   • Every response uses `@id` values anchored to https://vanturis.com/
 *     so Google can resolve the Organization ↔ WebSite ↔ WebPage ↔
 *     Breadcrumbs ↔ Product entity graph deterministically.
 *   • Helpers NEVER inject unverified facts.  If a caller does not have
 *     verified data (converter, mileage, price, etc.), the helper omits
 *     that key from the emitted JSON-LD.
 *   • Private/internal listing fields must NEVER be passed to these
 *     helpers — public listing data must first be projected through the
 *     backend public serializer.
 */

const BASE = "https://vanturis.com";

const ORG_REF = { "@id": `${BASE}/#organization` };
const SITE_REF = { "@id": `${BASE}/#website` };

/**
 * Organization entity — describes Vanturis itself.  Only facts that are
 * verifiably true today are emitted.  Callers must NOT pass fabricated
 * social profile URLs — pass only real, already-live Vanturis-owned
 * accounts.  The helper drops empty / missing keys automatically.
 */
export function buildOrganizationLd({ sameAs } = {}) {
  const ld = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${BASE}/#organization`,
    name: "Vanturis",
    url: BASE,
    logo: `${BASE}/organization-logo.png`,
    description:
      "Vanturis is a premium marketplace for luxury motorcoaches, Prevost motorhomes, entertainer coaches, and specialty touring vehicles.",
  };
  if (Array.isArray(sameAs) && sameAs.length > 0) {
    ld.sameAs = sameAs.filter(
      (u) => typeof u === "string" && /^https?:\/\//.test(u)
    );
    if (ld.sameAs.length === 0) delete ld.sameAs;
  }
  return ld;
}

/** WebSite entity — supports the sitelinks searchbox where applicable. */
export function buildWebSiteLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${BASE}/#website`,
    url: BASE,
    name: "Vanturis",
    publisher: ORG_REF,
    inLanguage: "en-US",
  };
}

/** WebPage entity — used on every indexable public page. */
export function buildWebPageLd({ path, name, description, image }) {
  const url = `${BASE}${path || "/"}`;
  const ld = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name,
    description,
    isPartOf: SITE_REF,
    about: ORG_REF,
    inLanguage: "en-US",
  };
  if (image) {
    ld.primaryImageOfPage = { "@type": "ImageObject", url: image };
  }
  return ld;
}

/** BreadcrumbList — matches the visible breadcrumb trail. */
export function buildBreadcrumbLd(items) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.path?.startsWith("http") ? it.path : `${BASE}${it.path}`,
    })),
  };
}

/**
 * CollectionPage entity — used on category / directory pages that present
 * a curated set of listings or collections (e.g. /marketplace,
 * /collections, /prevost-motorhomes).  Anchored to the same Organization ↔
 * WebSite entity graph as WebPage.
 */
export function buildCollectionPageLd({ path, name, description, image }) {
  const url = `${BASE}${path || "/"}`;
  const ld = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${url}#collectionpage`,
    url,
    name,
    description,
    isPartOf: SITE_REF,
    about: ORG_REF,
    inLanguage: "en-US",
  };
  if (image) ld.primaryImageOfPage = { "@type": "ImageObject", url: image };
  return ld;
}

/**
 * ItemList entity — enumerates the REAL, visible items on a collection or
 * category page.  Each entry needs a `name` and a `url` (site-relative or
 * absolute).  Returns null when there are no usable items so empty pages
 * never emit an empty list.  Never fabricate items.
 */
export function buildItemListLd(items, { name } = {}) {
  if (!Array.isArray(items)) return null;
  const elements = items
    .filter((it) => it && it.name && it.url)
    .map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: it.url.startsWith("http") ? it.url : `${BASE}${it.url}`,
    }));
  if (elements.length === 0) return null;
  const ld = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    numberOfItems: elements.length,
    itemListElement: elements,
  };
  if (name) ld.name = name;
  return ld;
}

/** FAQ — visible questions and answers on the same page. */
export function buildFaqLd(faqs) {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/**
 * Product/Vehicle listing structured data.  Only fields verified by
 * the public listing serializer are emitted.  Vanturis is described as
 * the platform hosting the listing, NOT the seller — the caller must
 * supply the seller's display name when representing the offer.
 *
 * Never call with private listing fields (seller_id, contact info,
 * admin notes, etc.) — pass only the public serializer's projection.
 */
export function buildListingProductLd(listing) {
  if (!listing || !listing.title) return null;
  const slug = listing.public_slug || listing.id;
  if (!slug) return null;
  const url = `${BASE}/marketplace/${slug}`;
  const brand = listing.manufacturer || listing.chassis_make;
  const model = listing.model || listing.chassis_model;
  const ld = {
    "@context": "https://schema.org",
    "@type": "Vehicle",
    "@id": `${url}#vehicle`,
    name: listing.title,
    url,
  };
  if (brand) ld.brand = { "@type": "Brand", name: brand };
  if (model) ld.model = model;
  if (listing.year) ld.vehicleModelDate = String(listing.year);
  if (listing.mileage != null && Number.isFinite(listing.mileage)) {
    ld.mileageFromOdometer = {
      "@type": "QuantitativeValue",
      value: listing.mileage,
      unitCode: "SMI",
    };
  }
  if (listing.fuel) ld.fuelType = listing.fuel;
  if (listing.transmission) ld.vehicleTransmission = listing.transmission;
  if (Array.isArray(listing.images) && listing.images.length > 0) {
    ld.image = listing.images.slice(0, 6);
  } else if (listing.hero_image) {
    ld.image = [listing.hero_image];
  }
  if (listing.description) ld.description = listing.description;
  // Offer — only when the listing carries a real numeric price and is
  // not marked call-for-price.
  if (
    typeof listing.price === "number" &&
    listing.price > 0 &&
    listing.price_state === "priced" &&
    !listing.sold_status
  ) {
    ld.offers = {
      "@type": "Offer",
      url,
      priceCurrency: "USD",
      price: listing.price,
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/UsedCondition",
      seller: listing.listing_party_display_name
        ? { "@type": "Organization", name: listing.listing_party_display_name }
        : undefined,
    };
    // Strip undefined seller so JSON-LD stays clean.
    if (!ld.offers.seller) delete ld.offers.seller;
  }
  return ld;
}
